// Pestaña Stock de la fila PCF: saldo, stock por tienda y últimos lotes; con permiso, «Registrar movimiento».
import { useMemo, useState } from 'react';
import { CircleX, PackagePlus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorState, Skeleton } from '@/kit';
import { userMessageOf } from '@/api/errors';
import { CellText, DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import {
    RegisterCardBatchModal,
    WriteOffCardsModal,
} from '@/domains/retail/tarjetas/components/CardStockModals';
import { useCardLedger } from '../../api/useCardLedger';
import { CARD_STOCK_QUERY_KEY } from '../../api/useInventarioLens';
import { formatInt } from '../../lib/inventoryFormat';
import {
    cardsByStore,
    storeInfo,
    storeOptions,
    writeOffStoreOptions,
    type StoreDirectory,
} from '../../lib/inventoryDetail';
import {
    cardBalanceTotals,
    cardStoreBalances,
    latestBatchEntries,
    mergeCardLedger,
    type CardStoreBalance,
} from '../../lib/inventoryLedger';
import type { InventoryRow } from '../../lib/inventoryList';
import { useInventoryAbilities } from '../../lib/inventoryPermissions';
import { DetailCard, DetailEmpty } from './DetailCard';
import { LedgerTable } from './LedgerTable';
import { MovementActionsCard, type MovementAction } from './MovementActionsCard';

type CardAction = 'REGISTER' | 'WRITE_OFF';

export interface CardStockContext {
    retailerId: string | null;
    stores: StoreDirectory;
    /** Tienda del filtro; ausente en consolidado. */
    storeId: string | undefined;
}

const ACTIONS: readonly MovementAction<CardAction>[] = [
    {
        key: 'REGISTER',
        icon: PackagePlus,
        title: 'Agregar tarjetas',
        description: 'Llegó un lote del proveedor a una tienda. Suma al saldo por cantidad.',
        accent: 'border-l-success',
    },
    {
        key: 'WRITE_OFF',
        icon: CircleX,
        title: 'Dar de baja',
        description: 'Extravío, robo o plástico dañado. Resta del disponible de la tienda.',
        accent: 'border-l-danger',
    },
];

/** Libro de tarjetas del contexto: la tienda del filtro o todas las del padrón. */
export function useContextCardLedger(ctx: CardStockContext) {
    const storeIds = useMemo(
        () => (ctx.storeId ? [ctx.storeId] : storeOptions(ctx.stores).map((o) => o.value)),
        [ctx.storeId, ctx.stores],
    );
    const ledger = useCardLedger(ctx.retailerId, storeIds);
    const entries = useMemo(() => mergeCardLedger(ledger.data?.pages ?? []), [ledger.data]);
    return { ledger, entries };
}

function storeBalanceColumns(stores: StoreDirectory): DataTableColumn<CardStoreBalance>[] {
    return [
        {
            key: 'store',
            label: 'Tienda',
            width: '46%',
            cell: (b) => {
                const store = storeInfo(stores, b.storeId);
                return (
                    <span className="flex min-w-0 items-baseline gap-1">
                        <CellText className="font-semibold">{store.name}</CellText>
                        <span className="flex-none font-mono text-[9.5px] text-muted-foreground">
                            {store.code}
                        </span>
                    </span>
                );
            },
            cellTitle: (b) => storeInfo(stores, b.storeId).name,
        },
        {
            key: 'available',
            label: 'Disponibles',
            width: '18%',
            numeric: true,
            cell: (b) => formatInt(b.available),
            cellClassName: () => 'font-bold',
        },
        {
            key: 'delivered',
            label: 'Entregadas',
            width: '18%',
            numeric: true,
            cell: (b) => formatInt(b.delivered),
        },
        {
            key: 'writtenOff',
            label: 'Dadas de baja',
            width: '18%',
            numeric: true,
            cell: (b) => formatInt(b.writtenOff),
        },
    ];
}

function Totals({ totals }: { totals: ReturnType<typeof cardBalanceTotals> }) {
    const items = [
        { label: 'Disponibles', value: totals.available },
        { label: 'Entregadas', value: totals.delivered },
        { label: 'Dadas de baja', value: totals.writtenOff },
    ];
    return (
        <dl className="grid grid-cols-3 gap-2">
            {items.map((item) => (
                <div key={item.label} className="flex min-w-0 flex-col gap-0.5">
                    <dt className="truncate text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                        {item.label}
                    </dt>
                    <dd className="text-[12px] font-bold tabular-nums text-foreground">
                        {formatInt(item.value)}
                    </dd>
                </div>
            ))}
        </dl>
    );
}

export function CardStockTab({ row, ctx }: { row: InventoryRow; ctx: CardStockContext }) {
    const { canRegisterCards, canWriteOffCards } = useInventoryAbilities();
    const queryClient = useQueryClient();
    const [open, setOpen] = useState<CardAction | null>(null);
    const available = useMemo(() => cardsByStore(row), [row]);
    const { ledger, entries } = useContextCardLedger(ctx);
    const balances = useMemo(
        () =>
            cardStoreBalances(ledger.data?.pages[0]).sort((a, b) =>
                storeInfo(ctx.stores, a.storeId).name.localeCompare(
                    storeInfo(ctx.stores, b.storeId).name,
                    'es',
                ),
            ),
        [ledger.data, ctx.stores],
    );
    const allowed = ACTIONS.filter((a) =>
        a.key === 'REGISTER' ? canRegisterCards : canWriteOffCards,
    ).map((a) =>
        a.key === 'WRITE_OFF' && row.stock === 0
            ? { ...a, disabledReason: 'No hay tarjetas disponibles' }
            : a,
    );
    const refresh = (): void => {
        void queryClient.invalidateQueries({ queryKey: CARD_STOCK_QUERY_KEY });
    };

    if (ctx.retailerId === null) {
        return (
            <DetailCard title="Stock">
                <DetailEmpty>
                    No podemos leer las tarjetas sin una cadena asignada a tu usuario.
                </DetailEmpty>
            </DetailCard>
        );
    }
    const retailerId = ctx.retailerId;
    const readState = ledger.error ? (
        <ErrorState
            className="m-3"
            title="No se pudo leer el saldo de tarjetas"
            description={userMessageOf(ledger.error)}
            onRetry={() => void ledger.refetch()}
        />
    ) : ledger.isPending ? (
        <div className="p-3">
            <Skeleton lines={3} />
        </div>
    ) : null;
    return (
        <>
            {allowed.length > 0 ? (
                <MovementActionsCard actions={allowed} onPick={setOpen} />
            ) : (
                <p className="mb-2 px-0.5 text-[10.5px] italic text-muted-foreground">
                    Tu rol solo consulta las tarjetas: agregarlas o darlas de baja requiere otro
                    permiso.
                </p>
            )}
            <DetailCard title="Saldo de tarjetas" aside="tiempo real" flush={readState !== null}>
                {readState ?? <Totals totals={cardBalanceTotals(balances)} />}
            </DetailCard>
            {readState === null ? (
                <>
                    <DetailCard title="Stock por tienda" flush>
                        <DataTable
                            caption="Tarjetas por tienda"
                            columns={storeBalanceColumns(ctx.stores)}
                            rows={balances}
                            rowKey={(b) => b.storeId}
                            density="compact"
                            empty="Sin tiendas en tu cadena."
                        />
                    </DetailCard>
                    <DetailCard title="Últimas entradas de lote" flush>
                        <LedgerTable
                            entries={latestBatchEntries(entries)}
                            stores={ctx.stores}
                            emptyText="Ninguna tienda ha recibido lotes de tarjetas."
                            showIdentifier={false}
                        />
                    </DetailCard>
                </>
            ) : null}
            {open === 'REGISTER' ? (
                <RegisterCardBatchModal
                    retailerId={retailerId}
                    storeId={ctx.storeId ?? ''}
                    storeOptions={storeOptions(ctx.stores)}
                    onRegistered={refresh}
                    onClose={() => {
                        setOpen(null);
                    }}
                />
            ) : null}
            {open === 'WRITE_OFF' ? (
                <WriteOffCardsModal
                    retailerId={retailerId}
                    storeId={ctx.storeId ?? ''}
                    storeOptions={writeOffStoreOptions(ctx.stores, available)}
                    availableIn={(storeId) => available.get(storeId) ?? 0}
                    onSettled={refresh}
                    onClose={() => {
                        setOpen(null);
                    }}
                />
            ) : null}
        </>
    );
}
